import React, { memo, useContext, useEffect, useState } from 'react';
import { RendererContext } from '../../contexts/RendererContext';
import { getWidgetVisibleProperty, getWidgetLayoutXProperty, getWidgetLayoutYProperty, getWidgetLayoutWidthProperty, getWidgetLayoutHeightProperty } from '../../modules/Properties/integration/WidgetSettingsIntegration';
import { BACKGROUND_HEX, RenderViewWidgetContents, readViewerThemeBackground, useDensityModeOverride, useReactiveValue } from './index';

type Phase = 'closed' | 'opening' | 'open' | 'closing';

/** Tools bar at the top of the viewport — widget can't overlap it. */
const TOOLS_BAR_HEIGHT = 52;

/** Collapsed-button rect, top-left of the viewport, mirroring the 16px left
 *  inset used by HintBar/ViewSelector with a matching gap below the toolbar. */
const BUTTON_RECT = { left: 16, top: TOOLS_BAR_HEIGHT + 16, width: 40, height: 40 };

/** Morph duration in ms. Must stay aligned with the Tailwind duration class below. */
const MORPH_MS = 300;

/**
 * Animated shell that morphs between a small top-left toggle button and the
 * full render-view widget at the user's saved layout. Owns the outer
 * container; the widget's canvas + orbit subtree is rendered inside via
 * `RenderViewWidgetContents` only while phase === 'open'.
 */
export const RenderViewMorphShell = memo(function RenderViewMorphShell() {
  const { viewer } = useContext(RendererContext);
  const visible = useReactiveValue<boolean>(getWidgetVisibleProperty);

  const layoutX = useReactiveValue<number>(getWidgetLayoutXProperty);
  const layoutY = useReactiveValue<number>(getWidgetLayoutYProperty);
  const layoutW = useReactiveValue<number>(getWidgetLayoutWidthProperty);
  const layoutH = useReactiveValue<number>(getWidgetLayoutHeightProperty);

  // Initialise phase from the persisted visibility — page-load with visible=true
  // shows the panel immediately, no morph plays.
  const [phase, setPhase] = useState<Phase>(visible ? 'open' : 'closed');

  // Sync `visible` flips into the morph state synchronously during render
  // (the "storing info from previous renders" pattern from the React docs —
  // same one `useDragResize` uses for its `initial` prop). React discards
  // the in-progress render and re-runs with the new phase, so the next
  // paint is at the morph's starting state. No refs, no setState in effects.
  const [lastSyncedVisible, setLastSyncedVisible] = useState(visible);
  if (lastSyncedVisible !== visible) {
    setLastSyncedVisible(visible);
    if (visible && phase !== 'open' && phase !== 'opening') {
      setPhase('opening');
    } else if (!visible && phase !== 'closed' && phase !== 'closing') {
      setPhase('closing');
    }
  }

  // Advance `opening` → `open` and `closing` → `closed` after the CSS
  // transition finishes. setState happens inside the timeout callback, not
  // the effect body, so the set-state-in-effect lint is satisfied.
  useEffect(() => {
    if (phase !== 'opening' && phase !== 'closing') return undefined;
    const next: Phase = phase === 'opening' ? 'open' : 'closed';
    const timer = window.setTimeout(() => {
      setPhase((p) => (p === phase ? next : p));
    }, MORPH_MS + 20);
    return () => window.clearTimeout(timer);
  }, [phase]);

  // Background tracking — copied verbatim from the original widget. The shell
  // paints its own background so the color interpolates across the morph.
  const renderView = viewer ? viewer.ensureWidgetView() : null;
  const widgetIsXray = useDensityModeOverride(renderView?.viewState ?? null) === true;
  const [themeBg, setThemeBg] = useState<'dark' | 'light'>(() => readViewerThemeBackground(viewer));
  const [lastViewer, setLastViewer] = useState(viewer);
  if (lastViewer !== viewer) {
    setLastViewer(viewer);
    setThemeBg(readViewerThemeBackground(viewer));
  }
  useEffect(() => {
    if (!viewer) return;
    const resync = () => setThemeBg(readViewerThemeBackground(viewer));
    viewer.addEventListener('background_changed', resync);
    const observer = new MutationObserver(resync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      viewer.removeEventListener('background_changed', resync);
      observer.disconnect();
    };
  }, [viewer]);
  const bgKey = widgetIsXray ? 'black' : themeBg;
  const panelBg = BACKGROUND_HEX[bgKey];

  // Compute the container's inline style from phase.
  // - closed → BUTTON_RECT
  // - opening → BUTTON_RECT for the first frame, then panel rect on rAF so
  //   the CSS transition picks up a delta (rAF guarantees the browser has
  //   committed the initial style before we change it).
  // - open / closing(first frame) → panel rect
  // - closing → panel rect for the first frame, then BUTTON_RECT on rAF
  //
  // Sync the synchronous part during render (same pattern as the phase
  // machine); schedule the rAF swap from an effect so refs aren't touched
  // during render.
  const [styleTarget, setStyleTarget] = useState<'button' | 'panel'>(visible ? 'panel' : 'button');
  const [lastPhase, setLastPhase] = useState(phase);
  if (lastPhase !== phase) {
    setLastPhase(phase);
    if (phase === 'opening') setStyleTarget('button');
    else if (phase === 'closing') setStyleTarget('panel');
    else setStyleTarget(phase === 'open' ? 'panel' : 'button');
  }

  // Swap to the destination style one frame after entering opening/closing
  // so the CSS transition sees a non-trivial delta to interpolate.
  useEffect(() => {
    if (phase !== 'opening' && phase !== 'closing') return undefined;
    const target: 'button' | 'panel' = phase === 'opening' ? 'panel' : 'button';
    const id = requestAnimationFrame(() => setStyleTarget(target));
    return () => cancelAnimationFrame(id);
  }, [phase]);

  // Clamp the panel's top so the morph target never animates underneath the
  // tools bar — `useDragResize` clamps live gestures, but a stale cloud-synced
  // layoutY (saved before this clamp existed) could still seed a too-high value.
  const panelTop = Math.max(TOOLS_BAR_HEIGHT, layoutY);
  const containerStyle: React.CSSProperties =
    styleTarget === 'panel'
      ? { left: layoutX, top: panelTop, width: layoutW, height: layoutH, backgroundColor: panelBg }
      : { left: BUTTON_RECT.left, top: BUTTON_RECT.top, width: BUTTON_RECT.width, height: BUTTON_RECT.height, backgroundColor: panelBg };

  // Animate during morph; disable transition while fully open so drag/resize
  // doesn't lag behind the cursor.
  const transitionClass = phase === 'opening' || phase === 'closing' ? 'transition-all duration-300 ease-out' : 'transition-none';

  // Click only acts when collapsed — otherwise the widget body handles its
  // own interactions (drag, close button, etc.).
  const onClick = () => {
    if (phase !== 'closed') return;
    getWidgetVisibleProperty().value = true;
  };

  const isButton = phase === 'closed';
  const glyphVisible = phase !== 'open';
  const glyphOpacity = phase === 'closed' ? 'opacity-100' : 'opacity-0';

  return (
    <div
      role={isButton ? 'button' : undefined}
      tabIndex={isButton ? 0 : -1}
      aria-label={isButton ? 'Show secondary view' : undefined}
      title={isButton ? 'Show secondary view' : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (isButton && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={'absolute z-30 overflow-hidden rounded-md shadow-md ring-1 ring-black/30 dark:ring-white/15 ' + (isButton ? 'cursor-pointer hover:ring-neon-green-300 ' : '') + transitionClass}
      style={containerStyle}
    >
      {/* Glyph — visible while morphing or collapsed, hidden once open. */}
      {glyphVisible && (
        <div className={'absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-150 ' + glyphOpacity + ' text-gray-800 dark:text-gray-200'}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="3" width="11" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="7" y="8" width="11" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity={visible ? 0.2 : 0} />
          </svg>
        </div>
      )}

      {/* Widget contents — mounted only after morph-in completes so the canvas
          attaches with correct final dimensions. */}
      {phase === 'open' && <FadeInContents />}
    </div>
  );
});

/**
 * Mounts the widget body and fades it in on the frame after mount. Separate
 * component so the rAF effect runs once per `open` phase entry.
 */
const FadeInContents = memo(function FadeInContents() {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div className={'absolute inset-0 transition-opacity duration-150 ' + (shown ? 'opacity-100' : 'opacity-0')}>
      <RenderViewWidgetContents />
    </div>
  );
});
